import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { first, get } from 'lodash';
import { useDispatch } from 'react-redux';
import { TP } from '../../../constants/consts';
import SubmitModalLayout from '../../../components/SubmitModalLayout';
import Button from '../../../components/Button';
import FormikAutocompleteSelect from '../../../components/Fields/FormikAutocompleteSelect';
import { cardFields } from '../constants';
import { getOption } from '../../../utils/common';
import CheckboxField from '../../../components/Fields/CheckboxField';
import { useTranslation } from '../../../src/i18n';
import VenuesFieldsGroup from '../../../components/EditComponents/VenuesFieldsGroup';
import { updateOne, useBufferValueAction } from '../../App/actions';
import { VENUES } from '../../../constants/endpointConstants';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
    marginBottom: 150,
  },
  form: {
    margin: 0,
    width: '100%',
  },
  modal: {
    maxWidth: '80vw',
    '& .MuiGrid-item': {
      maxWidth: '50%',
      flexBasis: '50%',
      flexGrow: 1,
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        maxWidth: '100%',
        flexBasis: '100%',
      },
    },
  },
  defaultVenuesSelect: {
    marginBottom: theme.spacing(2),
    wordBreak: 'break-word',
  },
  venueField: {
    width: '80%',
  },
}));

export const getDefaultVenue = venue => ({
  [cardFields.city.name]: venue?.city?.id ? getOption(venue?.city) : '',
  [cardFields.country.name]: venue?.country?.id ? getOption(venue?.country) : '',
  [cardFields.venue.name]: venue?.id ? getOption(venue) : null,
  [cardFields.stage.name]: venue?.stages?.length > 0 ? getOption(first(venue?.stages)) : '',
});

const getDefaultVenueOption = venue => ({
  ...venue,
  label: `${venue?.name} (${venue?.country?.name}, ${venue?.city?.name})`,
  value: venue?.id,
});

const StandardizedVenue = ({
  loadingValue,
  searchCities,
  searchVenue,
  venues,
  cities,
  clearValues,
  setDefaultVenue,
  defaultValues,
  searchStages,
  stages,
  onClose,
  companyVenues,
  isOpened,
}) => {
  const { t } = useTranslation();
  const BulkVenueRef = React.createRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isCompanyVenueCheck, setIsCompanyVenueCheck] = React.useState(false);
  const [defaultVenues] = React.useState(companyVenues.map(cVenue => getDefaultVenueOption(cVenue)));
  const [defaultVenue, setDefaultFormVenue] = React.useState(getDefaultVenueOption(first(companyVenues)));

  const changeDefaultVenue = venueOption => {
    setDefaultFormVenue(venueOption);
    const updateForm = BulkVenueRef?.current?.setValues;
    if (updateForm) {
      updateForm({ data: getDefaultVenue(venueOption) });
    }
  };

  const submitForm = values => {
    // eslint-disable-next-line no-underscore-dangle
    if (values?.data?.stage_id?.__isNew__) {
      const venueId = values.data[cardFields.venue.name]?.value;
      dispatch(
        updateOne({
          endpoint: VENUES,
          sagaRoutine: useBufferValueAction,
          id: venueId,
          params: {
            stages: [
              ...get(values, `data.${cardFields.venue.name}.stages`, []).map(stage => ({ id: stage?.id })),
              { name: get(values, `data.${cardFields.stage.name}.value`) },
            ],
          },
          callback: updatedVenue => {
            setDefaultVenue({
              ...values?.data,
              stage_id: getOption(updatedVenue?.stages.find(el => el?.name === values?.data?.stage_id?.value)),
            });
          },
          successMessage: `Stage ${get(values, `data.${cardFields.stage.name}.value`)} was created`,
        }),
      );
    } else {
      setDefaultVenue(values?.data);
    }
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      title1={t(`${TP}.FN_EDITSEASON_STANDART_VENUE`)}
      buttonText={t(`${TP}.FN_APPLY_TO_ALL_PERFORMANCES_STANDARD`)}
      onClose={onClose}
      footer={
        <Button onClick={() => BulkVenueRef?.current?.submitForm()}>
          {t(`${TP}.FN_APPLY_TO_ALL_PERFORMANCES_STANDARD`)}
        </Button>
      }
      className={classes.modal}
    >
      <div className={classes.content}>
        {companyVenues?.length > 0 && (
          <div className={classes.defaultVenuesSelect}>
            <CheckboxField
              instanceId="isCompanyVenue"
              checked={isCompanyVenueCheck}
              onChange={setIsCompanyVenueCheck}
              name="isCompanyVenue"
              label={t(`${TP}.FN_CHOOSE_FROM_VENUES`)}
            />
            {isCompanyVenueCheck && (
              <FormikAutocompleteSelect
                instanceId="defaultVenuesList"
                field={{
                  name: 'defaultVenuesList',
                  value: defaultVenue,
                }}
                onChange={changeDefaultVenue}
                preventDefaultOnChange
                options={defaultVenues}
                withFiltering
                className={classes.venueField}
              />
            )}
          </div>
        )}
        <Formik initialValues={{ data: defaultValues }} onSubmit={submitForm} innerRef={BulkVenueRef}>
          {({ values, setFieldValue }) => (
            <Form>
              <VenuesFieldsGroup
                parentFieldName="data"
                loadingValue={loadingValue}
                searchVenue={searchVenue}
                searchCities={searchCities}
                setFieldValue={setFieldValue}
                venues={venues}
                cities={cities}
                values={values?.data || {}}
                clearValues={clearValues}
                searchStages={searchStages}
                stages={stages}
                className={classes.form}
                isStageCreatable
              />
            </Form>
          )}
        </Formik>
      </div>
    </SubmitModalLayout>
  );
};

StandardizedVenue.propTypes = {
  loadingValue: PropTypes.string,
  searchVenue: PropTypes.func.isRequired,
  searchCities: PropTypes.func.isRequired,
  searchStages: PropTypes.func.isRequired,
  venues: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  stages: PropTypes.array.isRequired,
  clearValues: PropTypes.func.isRequired,
  setDefaultVenue: PropTypes.func.isRequired,
  defaultValues: PropTypes.object.isRequired,
};

export default StandardizedVenue;
